import React, { useState } from 'react';
import { 
  GraduationCap, Play, Pause, CheckCircle2, Search, Filter, Volume2, 
  Sparkles, Award, FileText, Globe, BookOpen, ShieldCheck, Download, ChevronRight, Activity, Brain
} from 'lucide-react';
import { MODULE_TUTORIALS, ModuleTutorial } from '../data/moduleTutorialData';
import { DISABILITY_CLUSTERS, DisabilityCategory } from '../data/disabilityTaxonomy';

export const ModuleTutorialHub: React.FC = () => {
  const [selectedTutorialId, setSelectedTutorialId] = useState<string>(MODULE_TUTORIALS[0].id);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClusterFilter, setSelectedClusterFilter] = useState<string>('all');
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const activeTutorial = MODULE_TUTORIALS.find(t => t.id === selectedTutorialId) || MODULE_TUTORIALS[0];

  const handleTogglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const filteredTutorials = MODULE_TUTORIALS.filter(t => {
    const matchesSearch = t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.category.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    if (selectedClusterFilter === 'all') return true;
    const cluster = DISABILITY_CLUSTERS.find(c => c.id === selectedClusterFilter);
    if (!cluster) return true;
    return cluster.primaryModuleIds.includes(t.id) || cluster.secondaryModuleIds.includes(t.id);
  });

  const matchingClusters = DISABILITY_CLUSTERS.filter(c => 
    c.primaryModuleIds.includes(activeTutorial.id) || c.secondaryModuleIds.includes(activeTutorial.id)
  );

  return (
    <div className="space-y-6 max-w-7xl mx-auto py-4 px-2">
      
      {/* Clarvos Tutorial Hub Hero Header */}
      <div className="bg-[#1c1b1f] text-white p-7 sm:p-8 rounded-[32px] shadow-2xl border border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative overflow-hidden backdrop-blur-xl">
        <div className="space-y-3 text-left z-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-[#ffd64e]/10 text-[#ffd64e] text-xs font-black rounded-full border border-[#ffd64e]/30 uppercase tracking-wider">
            <GraduationCap className="w-4 h-4 text-[#ffd64e]" />
            <span>Interactive 30-Module Video & Clinical Tutorial Hub</span>
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-white">
            Master Every Module, Advantage & Impact
          </h1>
          <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
            Comprehensive operational guides, therapeutic advantages, expected clinical outputs, and regulatory compliance alignment (RPwD Act 2016, NEP 2020, UDID, NDIS, IDEA 2004).
          </p>
        </div>

        <div className="flex items-center gap-3 bg-white/5 p-3 rounded-[24px] border border-white/10 backdrop-blur-md shrink-0 z-10">
          <div className="text-center px-4 border-r border-white/10">
            <div className="text-2xl font-black text-[#ffd64e] font-mono">30 / 30</div>
            <div className="text-[10px] uppercase font-bold tracking-wider text-white/60">Modules Covered</div>
          </div>
          <button
            onClick={() => window.print()}
            className="px-5 py-2.5 bg-[#ffd64e] hover:bg-[#ffcf33] text-[#1c1b1f] font-black text-xs rounded-full shadow-md transition flex items-center gap-1.5"
          >
            <Download className="w-4 h-4 text-[#1c1b1f]" />
            <span>Export Manual PDF</span>
          </button>
        </div>
      </div>

      {/* Tutorial Explorer Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Sidebar: Module Selector List */}
        <div className="lg:col-span-4 bg-[#f9f6f0] p-5 rounded-[32px] border border-black/10 shadow-sm space-y-4">
          <div className="relative">
            <Search className="w-4 h-4 text-[#1c1b1f]/40 absolute left-3.5 top-3" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search module tutorial..."
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-black/10 rounded-full text-xs font-bold text-[#1c1b1f] placeholder:text-[#1c1b1f]/40 focus:ring-2 focus:ring-[#ffd64e] focus:outline-none"
            />
          </div>

          {/* Disability Condition Filter Pills */}
          <div className="flex flex-wrap gap-1.5 pb-1">
            <button
              onClick={() => setSelectedClusterFilter('all')}
              className={`px-3 py-1.5 rounded-full text-[10px] font-black transition-all ${
                selectedClusterFilter === 'all'
                  ? 'bg-[#1c1b1f] text-[#ffd64e] shadow-sm'
                  : 'bg-white text-[#1c1b1f]/70 border border-black/5 hover:bg-[#eae7e1]'
              }`}
            >
              All ({MODULE_TUTORIALS.length})
            </button>
            {DISABILITY_CLUSTERS.map((cl) => (
              <button
                key={cl.id}
                onClick={() => setSelectedClusterFilter(cl.id)}
                className={`px-3 py-1.5 rounded-full text-[10px] font-black transition-all flex items-center gap-1 ${
                  selectedClusterFilter === cl.id
                    ? 'bg-[#1c1b1f] text-[#ffd64e] shadow-sm'
                    : 'bg-white text-[#1c1b1f]/70 border border-black/5 hover:bg-[#eae7e1]'
                }`}
              >
                <span>{cl.shortName}</span>
              </button>
            ))}
          </div>

          <div className="space-y-2 max-h-[620px] overflow-y-auto pr-1">
            {filteredTutorials.map((tut) => {
              const isSelected = selectedTutorialId === tut.id;
              return (
                <button
                  key={tut.id}
                  onClick={() => {
                    setSelectedTutorialId(tut.id);
                    setActiveStep(0);
                    setIsPlaying(false);
                  }}
                  className={`w-full p-3.5 rounded-[22px] border text-left transition-all flex items-center justify-between gap-3 ${
                    isSelected 
                      ? 'bg-[#1c1b1f] text-white border-black/30 shadow-md ring-2 ring-[#ffd64e]/50' 
                      : 'bg-white text-[#1c1b1f] border-black/5 hover:bg-[#eae7e1]/40'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-8 h-8 rounded-xl font-mono font-black text-xs flex items-center justify-center shrink-0 ${
                      isSelected ? 'bg-[#ffd64e] text-[#1c1b1f]' : 'bg-[#eae7e1] text-[#1c1b1f]'
                    }`}>
                      {tut.moduleNumber}
                    </span>
                    <div className="truncate">
                      <div className={`text-xs font-black truncate ${isSelected ? 'text-white' : 'text-[#1c1b1f]'}`}>{tut.title}</div>
                      <div className={`text-[10px] font-medium truncate ${isSelected ? 'text-white/60' : 'text-[#1c1b1f]/60'}`}>
                        {tut.category} • {tut.videoDuration}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 shrink-0 ${isSelected ? 'text-[#ffd64e]' : 'text-black/30'}`} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Detail Pane: 4 Educational Pillars & Video Player */}
        <div className="lg:col-span-8 space-y-6">
          
          {/* Simulated Interactive Video Player */}
          <div className="bg-[#1c1b1f] text-white rounded-[32px] border border-white/10 p-6 sm:p-7 shadow-2xl space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/10 pb-3">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#ffd64e] animate-pulse" />
                <span className="text-xs font-black uppercase tracking-wider text-[#ffd64e]">
                  Module {activeTutorial.moduleNumber} Video Walkthrough Simulation
                </span>
              </div>
              <span className="text-xs font-mono font-bold text-white/50">{activeTutorial.videoDuration} HD</span>
            </div>

            {/* Target Clinical Clusters Bar */}
            {matchingClusters.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                <span className="text-[10px] font-mono text-white/50 font-bold uppercase tracking-wider">Target Disabilities:</span>
                {matchingClusters.map((cl) => {
                  const isPrimary = cl.primaryModuleIds.includes(activeTutorial.id);
                  return (
                    <span
                      key={cl.id}
                      className={`px-3 py-0.5 rounded-full text-[10px] font-black border ${
                        isPrimary
                          ? 'bg-[#ffd64e]/20 text-[#ffd64e] border-[#ffd64e]/40'
                          : 'bg-white/10 text-white/80 border-white/20'
                      }`}
                    >
                      {cl.shortName} {isPrimary ? '★ Primary' : '• Support'}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Video Stage Screen */}
            <div className="bg-gradient-to-br from-[#121114] to-[#25232a] rounded-[24px] border border-white/10 p-8 text-center min-h-[220px] flex flex-col items-center justify-center space-y-4 relative overflow-hidden">
              <div className="w-16 h-16 bg-[#ffd64e] text-[#1c1b1f] rounded-full flex items-center justify-center shadow-xl hover:scale-105 transition-transform cursor-pointer" onClick={handleTogglePlay}>
                <Play className="w-8 h-8 ml-1 text-[#1c1b1f]" />
              </div>

              <div className="space-y-1 max-w-md">
                <h4 className="text-base font-black text-white">
                  Step {activeTutorial.demoVideoSteps[activeStep]?.step || 1}: {activeTutorial.demoVideoSteps[activeStep]?.title}
                </h4>
                <p className="text-xs text-white/70">
                  {activeTutorial.demoVideoSteps[activeStep]?.description}
                </p>
              </div>

              {/* Step Dots */}
              <div className="flex items-center gap-2 pt-2">
                {activeTutorial.demoVideoSteps.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveStep(idx)}
                    className={`px-3.5 py-1 rounded-full text-[10px] font-black transition-all ${
                      activeStep === idx ? 'bg-[#ffd64e] text-[#1c1b1f] shadow' : 'bg-white/10 text-white/60 hover:bg-white/20'
                    }`}
                  >
                    Step {s.step}
                  </button>
                ))}
              </div>
            </div>

            {/* Video Controls Bar */}
            <div className="flex items-center justify-between gap-4 pt-2">
              <button
                onClick={handleTogglePlay}
                className="px-6 py-2.5 bg-[#ffd64e] hover:bg-[#ffcf33] text-[#1c1b1f] font-black rounded-full text-xs flex items-center gap-2 shadow-md transition"
              >
                {isPlaying ? <Pause className="w-4 h-4 text-[#1c1b1f]" /> : <Play className="w-4 h-4 text-[#1c1b1f]" />}
                <span>{isPlaying ? 'Pause Simulation' : 'Play Guided Walkthrough'}</span>
              </button>

              <div className="text-xs text-white/50 font-mono">
                {activeTutorial.title}
              </div>
            </div>
          </div>

          {/* 4 Pillars Grid: Operations, Advantages, Outputs, Regulatory Impact */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            
            {/* PILLAR 1: Operations */}
            <div className="bg-[#f9f6f0] p-6 rounded-[28px] border border-black/10 shadow-sm space-y-3.5">
              <div className="flex items-center gap-2 text-[#1c1b1f] font-black text-sm">
                <BookOpen className="w-4 h-4 text-[#1c1b1f]" />
                <h3>1. Module Operations & Step-by-Step</h3>
              </div>
              <ul className="space-y-2 text-xs text-[#1c1b1f]/80 font-medium">
                {activeTutorial.operations.map((op, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#1c1b1f] shrink-0 mt-0.5" />
                    <span>{op}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* PILLAR 2: Clinical Advantages */}
            <div className="bg-[#f9f6f0] p-6 rounded-[28px] border border-black/10 shadow-sm space-y-3.5">
              <div className="flex items-center gap-2 text-emerald-800 font-black text-sm">
                <Sparkles className="w-4 h-4 text-emerald-700" />
                <h3>2. Clinical Advantages & Benefits</h3>
              </div>
              <ul className="space-y-2 text-xs text-[#1c1b1f]/80 font-medium">
                {activeTutorial.advantages.map((adv, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 shrink-0 mt-0.5" />
                    <span>{adv}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* PILLAR 3: Clinical Outputs */}
            <div className="bg-[#f9f6f0] p-6 rounded-[28px] border border-black/10 shadow-sm space-y-3.5">
              <div className="flex items-center gap-2 text-[#b45309] font-black text-sm">
                <FileText className="w-4 h-4 text-[#d97706]" />
                <h3>3. Expected Clinical Outputs</h3>
              </div>
              <ul className="space-y-2 text-xs text-[#1c1b1f]/80 font-medium">
                {activeTutorial.clinicalOutputs.map((out, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#d97706] shrink-0 mt-0.5" />
                    <span>{out}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* PILLAR 4: Regulatory Impact */}
            <div className="bg-[#f9f6f0] p-6 rounded-[28px] border border-black/10 shadow-sm space-y-3.5">
              <div className="flex items-center gap-2 text-[#1c1b1f] font-black text-sm">
                <Globe className="w-4 h-4 text-[#1c1b1f]" />
                <h3>4. Regulatory Impact & Standards</h3>
              </div>
              <ul className="space-y-2 text-xs text-[#1c1b1f]/80 font-medium">
                {activeTutorial.regulatoryImpact.map((reg, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <ShieldCheck className="w-3.5 h-3.5 text-[#1c1b1f] shrink-0 mt-0.5" />
                    <span>{reg}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>

        </div>

      </div>

    </div>
  );
};
